import React, { useMemo, useState } from 'react';
import Card from '../../ui/Card';

const formatTime = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const deviceLine = (di) => {
  if (!di) return 'Device unknown';
  const parts = [];
  if (di.deviceModel) parts.push(di.deviceModel);
  else if (di.deviceVendor && di.deviceVendor !== 'Unknown') parts.push(di.deviceVendor);
  if (di.os) parts.push(`${di.os}${di.osVersion ? ` ${di.osVersion}` : ''}`);
  if (di.browser) parts.push(di.browser);
  const line = parts.join(' / ');
  return line || 'Device unknown';
};

const reasonLabel = (r) => {
  switch (r) {
    case 'voluntary': return 'Left voluntarily';
    case 'closed-tab': return 'Closed tab';
    case 'backgrounded': return 'Backgrounded app';
    case 'network-lost': return 'Lost network';
    case 'connection-error': return 'Connection error';
    case 'removed-by-trainer': return 'Removed by trainer';
    default: return 'Unknown';
  }
};

const ParticipantRow = ({ p }) => {
  const initials = (p.name || '?').split(' ').map(s => s.charAt(0)).slice(0, 2).join('').toUpperCase();
  const dotColor = p.status === 'joined' ? 'bg-emerald-500'
    : p.status === 'removed' ? 'bg-purple-500'
    : 'bg-red-500';

  const statusLine = p.status === 'joined'
    ? `Joined ${formatTime(p.joinedAt)} · ${p.responseCount || 0} responses · ${p.engagementScore || 0}% engaged`
    : `Left ${formatTime(p.leftAt)}${p.leaveReason ? ` — ${reasonLabel(p.leaveReason)}` : ''}`;

  return (
    <li className="py-3 flex items-start gap-3">
      <div className="relative flex-shrink-0">
        <div className="w-9 h-9 rounded-full bg-neutral-100 text-neutral-700 text-xs font-semibold flex items-center justify-center">
          {initials}
        </div>
        <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${dotColor} ${p.status === 'joined' ? 'animate-pulse' : ''}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-neutral-900 truncate">{p.name || 'Anonymous'}</p>
          {p.disconnectCount > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">
              {p.disconnectCount}× drop
            </span>
          )}
        </div>
        <p className="text-xs text-neutral-500 truncate">
          <span className="mr-1">{p.deviceInfo?.deviceType === 'mobile' ? '📱' : p.deviceInfo?.deviceType === 'tablet' ? '📱' : '💻'}</span>
          {deviceLine(p.deviceInfo)}
          {p.deviceInfo?.network ? ` · ${p.deviceInfo.network}` : ''}
        </p>
        <p className="text-xs text-neutral-400 mt-0.5">{statusLine}</p>
      </div>
    </li>
  );
};

/**
 * Searchable participant list, split into Active / Left / Removed sections.
 */
const LiveParticipantList = ({ participants = [], embedded = false }) => {
  const [query, setQuery] = useState('');

  const { active, left, removed } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = (p) => !q
      || (p.name && p.name.toLowerCase().includes(q))
      || (p.email && p.email.toLowerCase().includes(q));
    const filtered = participants.filter(matches);
    return {
      active: filtered.filter(p => p.status === 'joined'),
      left: filtered.filter(p => p.status === 'left'),
      removed: filtered.filter(p => p.status === 'removed'),
    };
  }, [participants, query]);

  const body = (
    <div className="space-y-4">
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={`Search ${participants.length} participants by name or email…`}
        className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-neutral-50/50 placeholder:text-neutral-400"
      />

      <div className="max-h-[460px] overflow-y-auto pr-1">
        <div>
          <h4 className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">Active ({active.length})</h4>
          {active.length > 0 ? (
            <ul className="divide-y divide-neutral-100">
              {active.map(p => <ParticipantRow key={p._id} p={p} />)}
            </ul>
          ) : (
            <p className="text-xs text-neutral-400 italic py-2">No active participants</p>
          )}
        </div>

        {left.length > 0 && (
          <div className="mt-4">
            <h4 className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">Left ({left.length})</h4>
            <ul className="divide-y divide-neutral-100">
              {left.map(p => <ParticipantRow key={p._id} p={p} />)}
            </ul>
          </div>
        )}

        {removed.length > 0 && (
          <div className="mt-4">
            <h4 className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-1">Removed ({removed.length})</h4>
            <ul className="divide-y divide-neutral-100">
              {removed.map(p => <ParticipantRow key={p._id} p={p} />)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );

  if (embedded) return body;
  return <Card title="Participants" padding="md">{body}</Card>;
};

export default LiveParticipantList;
